import { Input, Radio, RadioGroup } from '@mui/joy';
import Button from '@mui/joy/Button';

import { useId, useState } from 'react';

import { ExpandableErrorAlert } from '@/components/ExpandableErrorAlert';
import { InstanceFlags, useInstanceFlag } from '@/hooks/useInstanceFlag';
import { useOrganizations } from '@/hooks/useSwr';
import OrganizationIcon from '@/icons/OrganizationIcon.svg?react';

function NameInput(props: {
  value: string;
  onChange: (value: string) => void;
  nameTitle: string;
}) {
  const id = useId();
  return (
    <div className="flex flex-col items-stretch gap-2">
      <label htmlFor={id} className="text-sm font-semibold">
        {props.nameTitle} name
      </label>
      <Input
        id={id}
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        startDecorator={<OrganizationIcon />}
        placeholder={`${props.nameTitle} name...`}
      />
    </div>
  );
}

function EditNameForm(props: {
  existingNames: string[];
  nameTitle: string;
  onSubmit: (name: string, isPersonal: boolean) => void;
  isSubmitting?: boolean;
  submitError?: Error;
  defaultName?: string;
  buttonLabel?: string;
}) {
  const personalOrgDisabled = useInstanceFlag(
    InstanceFlags.personal_orgs_disabled
  );
  const { data: organizations, isLoading: isLoadingOrganizations } =
    useOrganizations();
  const hasPersonalOrg =
    organizations?.some((org) => org.is_personal) && !isLoadingOrganizations;

  const [validationError, setValidationError] = useState<string | null>(null);

  const [name, setName] = useState<string>(props.defaultName ?? '');
  const [personal, setPersonal] = useState(false);

  const error = validationError ?? props.submitError;
  const specialChars = /[!@#$%^&*(),.?":{}|<>]/;
  return (
    <form
      className="flex flex-col items-stretch gap-6"
      onSubmit={async (e) => {
        e.preventDefault();

        if (name.trim() === '') {
          setValidationError('Name is required');
          return;
        }
        if (props.existingNames.includes(name)) {
          setValidationError('Name is already taken');
          return;
        } else if (specialChars.test(name)) {
          setValidationError('Name cannot contain special characters');
          return;
        } else {
          setValidationError(null);
        }

        await props.onSubmit(name, personal);
      }}
    >
      <NameInput value={name} onChange={setName} nameTitle={props.nameTitle} />
      {!hasPersonalOrg &&
        !personalOrgDisabled &&
        props.nameTitle === 'Organization' && (
          <div className="flex flex-col items-stretch gap-4">
            <label className="text-sm font-semibold">
              {props.nameTitle} type
            </label>
            <RadioGroup
              value={personal ? 'personal' : 'team'}
              onChange={(e) => setPersonal(e.target.value === 'personal')}
            >
              <Radio size="sm" value="personal" label="Personal" />
              <Radio size="sm" value="team" label="Team" />
            </RadioGroup>
          </div>
        )}
      {error != null && <ExpandableErrorAlert error={error} />}

      <div className="self-end">
        <Button type="submit" loading={props.isSubmitting}>
          {props.buttonLabel ?? 'Create'}
        </Button>
      </div>
    </form>
  );
}

export { EditNameForm };
